import moment from "moment";
import { delay } from "redux-saga";
import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeLatest,
} from "redux-saga/effects";
import { prop } from "ramda";
import { openModal } from "@quantum/ui";

import {
  abrirArquivo,
  adicionarMedidas,
  adicionarMedidasComDescricao,
  atualizarMedidasUsuario,
  definirCompartilhamentoDeArquivo,
  definirJWTExcel,
  definirMedidasSuportadasExcel,
  definirMedidasFixas,
  editarAtivoExcel,
  editarModoBuscaDoAtivoExcel,
  getArquivoEmCache,
  getAtivoExcel,
  getDataBaseDefault,
  getFiltrosTipoAtivoPermitidos,
  getMedidasSelecionadas,
  getModoDeBuscaDoAtivoExcel,
  iniciarApp,
  isAppIniciada,
  isFechamentoDeMesSelecionado,
  selecionarDataBase,
  selecionarFiltrosTipoAtivo,
} from "../state";

import {
  FORMATO_DATA_BANCO,
  TIPO_ARQUIVO_CONFIGURACAO_RELATORIO,
} from "../model";

import {
  carregaChavesI18n,
  carregarDetalhesUsuario,
  carregarDominio,
} from "./bootSaga";

function* carregarDados({ api, idioma }) {
  yield all([
    call(carregarDominio, { api }),
    call(carregaChavesI18n, { api }),
    call(carregarDetalhesUsuario, {
      api,
      idioma: idioma || "pt-BR",
    }),
  ]);

  if (!(yield select(isAppIniciada))) {
    const dataBaseDefault = yield select(getDataBaseDefault);
    yield put(selecionarDataBase(moment(dataBaseDefault, FORMATO_DATA_BANCO)));

    const filtros = yield select(getFiltrosTipoAtivoPermitidos);
    yield put(selecionarFiltrosTipoAtivo(filtros.map(prop("id"))));

    yield put(atualizarMedidasUsuario());
    yield take("CARREGAR_MEDIDAS_USUARIO");

    yield put(iniciarApp());
  }
}

export default function* comunicacaoExcelSaga({
  api,
  cliente,
  aoPerderConexao,
  aoReceberConfiguracoes,
}) {
  let ultimaMsgRecebida = null;

  yield fork(function*() {
    while (true) {
      const mensagens = yield call(cliente.receberMensagens);

      for (let msg of mensagens) {
        ultimaMsgRecebida = new Date();

        switch (msg.tipo) {
          case "PING":
            yield call(cliente.enviarPong);
            break;

          case "CONFIGURACOES": {
            yield call(aoReceberConfiguracoes, msg);
            yield put(definirMedidasSuportadasExcel(msg.medidasSuportadas));
            yield put(definirMedidasFixas([]));
            yield put(definirJWTExcel(msg.jwt));
            yield put(definirCompartilhamentoDeArquivo(false));
            yield call(carregarDados, { api, idioma: msg.idioma });
            break;
          }

          case "EDITAR_MEDIDAS": {
            const { ativo, medidas } = msg;
            if (medidas.length > 0) {
              yield fork(function*() {
                while (!(yield select(isAppIniciada))) yield delay(100);

                yield put(editarModoBuscaDoAtivoExcel("ATIVO"));
                yield put(editarAtivoExcel(ativo));
                if (medidas[0].dataBase) {
                  yield put(adicionarMedidas(medidas));
                  yield put(
                    selecionarDataBase(
                      moment(medidas[0].dataBase, FORMATO_DATA_BANCO)
                    )
                  );
                } else {
                  yield put(adicionarMedidasComDescricao(medidas));
                }
              });
            }
            break;
          }

          case "EDITAR_QTLINK": {
            const { ativo, arquivo } = msg;
            yield fork(function*() {
              while (!(yield select(isAppIniciada))) yield delay(100);

              yield put(editarModoBuscaDoAtivoExcel("GRUPO"));
              yield put(editarAtivoExcel(ativo));
              yield put(abrirArquivo(arquivo));
            });
            break;
          }

          default:
            break;
        }
      }

      yield delay(5000);
    }
  });

  yield fork(function*() {
    yield delay(30000);
    while (true) {
      if (
        ultimaMsgRecebida == null ||
        moment().diff(ultimaMsgRecebida, "seconds") > 30
      ) {
        yield call(cliente.enviarPing);
        yield delay(15000);
        if (
          ultimaMsgRecebida == null ||
          moment().diff(ultimaMsgRecebida, "seconds") > 30
        ) {
          aoPerderConexao();
        }
      }
      yield delay(1000);
    }
  });

  yield takeLatest("ENVIAR_MEDIDAS_PARA_EXCEL", function*() {
    const medidas = yield select(getMedidasSelecionadas);
    const fechamentoDeMes = yield select(isFechamentoDeMesSelecionado);
    const ativo = yield select(getAtivoExcel);
    const modoDeBusca = yield select(getModoDeBuscaDoAtivoExcel);

    switch (modoDeBusca) {
      case "ATIVO": {
        if (!ativo) {
          yield put(
            openModal("ModalErro", {
              erros: ["Informe o ativo ou a referência da célula"],
            })
          );
          return;
        }
        yield call(cliente.enviarMedidas, ativo, medidas);
        break;
      }

      case "GRUPO": {
        if (!ativo) {
          yield put(
            openModal("ModalErro", {
              erros: ["Informe o grupo ou a referência da célula"],
            })
          );
          return;
        }
        try {
          const arquivo = yield select(state => {
            const aberto = getArquivoEmCache(state);
            if (aberto) return aberto;

            const data = moment().format("YYYY-MM-DD hh:mm:ss");
            const nome = `Vínculo Relatório - ${data}`;
            return {
              id: null,
              nome,
              dataModificacao: null,
              tipoArquivo: TIPO_ARQUIVO_CONFIGURACAO_RELATORIO,
            };
          });

          yield call(
            api.salvarArquivoMedidas,
            arquivo,
            medidas,
            fechamentoDeMes
          );
          yield call(cliente.enviarQTLink, ativo, arquivo.nome);
        } catch (erro) {
          yield put(
            openModal("ModalErro", {
              erros: ["Erro ao salvar arquivo de vínculo"],
            })
          );
          return;
        }
        break;
      }

      default:
        break;
    }

    yield call(cliente.desconectar);
    yield delay(1000);
    window.close();
  });
}
